import { CollapsibleContent as Content } from '@radix-ui/react-collapsible'
import {
  type AnimationEvent,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useState,
} from 'react'
import { cn } from '#src/cn.ts'

export type CollapsibleContentProps = ComponentPropsWithoutRef<typeof Content>

// The height animation runs on the Radix element itself, so callers style the
// inner wrapper instead: padding or a gap on the animated box would be counted
// into `--radix-collapsible-content-height` and make the open state jump.
//
// `animate-none!` for reduced motion, not a bare `animate-none`: the
// `data-[state=…]:` rules compile to an attribute-qualified selector, which
// out-specifies anything the `motion-reduce:` media query can emit on its own,
// so without `!` the preference is silently ignored.
export const CollapsibleContent = forwardRef<
  ElementRef<typeof Content>,
  CollapsibleContentProps
>(({ className, children, ...props }, ref) => {
  // The height animation needs overflow clipped, but clipping also cuts off
  // anything a child lifts out of the box — a dnd-kit row dragged up toward a
  // card header is translated, not reparented, so it would vanish at the edge.
  // Clip only while the height is actually moving. Starting expanded keeps the
  // reduced-motion path correct, where no animation runs and no event fires.
  const [expanded, setExpanded] = useState(true)
  // Animation events bubble, so ignore any that a descendant fired.
  const isOwn = (event: AnimationEvent<HTMLDivElement>): boolean =>
    event.currentTarget === event.target

  return (
    <Content
      ref={ref}
      onAnimationStart={(event) => {
        if (isOwn(event)) {
          setExpanded(false)
        }
      }}
      onAnimationEnd={(event) => {
        if (isOwn(event)) {
          setExpanded(event.currentTarget.dataset.state === 'open')
        }
      }}
      className={cn(
        'motion-reduce:animate-none! min-h-0 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down data-[state=closed]:overflow-hidden',
        expanded ? 'overflow-visible' : 'overflow-hidden'
      )}
      {...props}
    >
      <div className={className}>{children}</div>
    </Content>
  )
})

CollapsibleContent.displayName = Content.displayName
